import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Car, Lock, Loader2, ArrowLeft, CheckCircle2, XCircle, Eye, EyeOff, AlertCircle
} from 'lucide-react';

// Validações de senha
const PASSWORD_RULES = [
  { id: 'length', label: 'Mínimo 8 caracteres', test: (pwd) => pwd.length >= 8 },
  { id: 'uppercase', label: 'Pelo menos 1 letra maiúscula', test: (pwd) => /[A-Z]/.test(pwd) },
  { id: 'number', label: 'Pelo menos 1 número', test: (pwd) => /[0-9]/.test(pwd) },
];

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [success, setSuccess] = useState(false);
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Validar token ao carregar
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setValidating(false);
        return;
      }
      
      try {
        const res = await axios.get(`${API}/auth/validate-reset-token/${token}`);
        setTokenValid(true);
        setUserEmail(res.data.email_masked);
        setUserName(res.data.name);
      } catch (error) {
        setTokenValid(false);
        toast.error(error.response?.data?.detail || 'Link inválido ou expirado');
      } finally {
        setValidating(false);
      }
    };
    
    validateToken();
  }, [token]);

  // Validar força da senha
  const getPasswordStrength = (pwd) => {
    return PASSWORD_RULES.map(rule => ({
      ...rule,
      valid: rule.test(pwd)
    }));
  };

  const isPasswordValid = (pwd) => {
    return PASSWORD_RULES.every(rule => rule.test(pwd));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isPasswordValid(password)) {
      toast.error('A senha não atende aos requisitos de segurança');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('As senhas não conferem');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      
      setSuccess(true);
      toast.success('Senha redefinida com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao redefinir senha');
    } finally {
      setLoading(false);
    }
  };

  // Loading
  if (validating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary mb-4" />
          <h2 className="text-xl font-semibold">Validando link...</h2>
          <p className="text-muted-foreground mt-2">Aguarde um momento</p>
        </Card>
      </div>
    );
  }

  // Token inválido ou ausente
  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Link Inválido</h2>
          <p className="text-muted-foreground mb-6">
            Este link de redefinição de senha é inválido ou já expirou.
          </p>
          
          <div className="space-y-3">
            <Button 
              className="w-full"
              onClick={() => navigate('/client/forgot-password')}
            >
              Solicitar novo link
            </Button>
            
            <Button 
              variant="ghost" 
              className="w-full"
              onClick={() => navigate('/client/login')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar para Login
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Sucesso
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Senha Redefinida!</h2>
          <p className="text-muted-foreground mb-6">
            Sua senha foi alterada com sucesso. Você já pode fazer login com a nova senha.
          </p>
          
          <Button 
            className="w-full"
            onClick={() => navigate('/client/login')}
          >
            Fazer Login
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8">
        {/* Header */}
        <div className="flex items-center justify-center mb-4">
          <Car className="w-12 h-12 text-primary" />
        </div>
        <h1 className="text-2xl font-bold text-center mb-1">Nova Senha</h1>
        <p className="text-center text-muted-foreground mb-2">
          Olá, {userName}!
        </p>
        <p className="text-center text-sm text-muted-foreground mb-6">
          Conta: {userEmail}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="password">Nova Senha</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Digite sua nova senha"
                className="pl-10 pr-10"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="reset-password-input"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            
            {/* Requisitos de senha */}
            {password && (
              <div className="mt-2 space-y-1">
                {getPasswordStrength(password).map(rule => (
                  <div key={rule.id} className={`text-xs flex items-center gap-1 ${rule.valid ? 'text-green-600' : 'text-muted-foreground'}`}>
                    {rule.valid ? (
                      <CheckCircle2 className="w-3 h-3" />
                    ) : (
                      <XCircle className="w-3 h-3 text-red-400" />
                    )}
                    {rule.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="confirm-password">Confirmar Senha</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Repita a nova senha"
                className="pl-10 pr-10"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                data-testid="reset-confirm-input"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {confirmPassword && (
              <p className={`text-xs mt-1 flex items-center gap-1 ${
                password === confirmPassword ? 'text-green-600' : 'text-red-600'
              }`}>
                {password === confirmPassword ? (
                  <><CheckCircle2 className="w-3 h-3" /> Senhas conferem</>
                ) : (
                  <><XCircle className="w-3 h-3" /> Senhas não conferem</>
                )}
              </p>
            )}
          </div>

          <Button 
            type="submit" 
            className="w-full" 
            disabled={loading || !isPasswordValid(password) || password !== confirmPassword}
            data-testid="reset-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Salvando...
              </>
            ) : (
              'Redefinir Senha'
            )}
          </Button>

          <Button 
            type="button"
            variant="ghost" 
            className="w-full"
            onClick={() => navigate('/client/login')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar para Login
          </Button>
        </form>
      </Card>
    </div>
  );
}
